import type { ChangeEvent } from 'react'

import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
	InlineEdit,
	InlineEditInput,
	InlineEditTrigger,
} from '@/shared/components/ui/inline-edit'
import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/lib/utils'
import { asNumber } from '@/shared/tools/utils'

import { BasicNumberField } from './BasicNumberField'

const getValue = (editValue: string | number | null) => {
	if (editValue === '' || editValue === null) return null

	return asNumber(editValue as string)
}

export function BasicNumberEditorField(props: {
	value: number | null
	originalValue: number | null
	isDirty: boolean
	onChange: (value: number) => void
	className?: string
	disabled?: boolean
	disabledReason?: string
}) {
	const [editValue, setEditValue] = useState<string | number | null>(
		props.value
	)
	useEffect(() => {
		setEditValue(props.value)
	}, [props.originalValue])

	const isEdited = !!props?.isDirty

	const onChange = (event: ChangeEvent<HTMLInputElement>) =>
		setEditValue(event.target.value)
	const onConfirm = () => {
		const value = getValue(editValue) as number
		if (value === props.value) {
			return
		}
		props.onChange?.(value)
		setEditValue(null)
	}

	return (
		<InlineEdit onConfirm={onConfirm}>
			{props.value === null ? (
				<InlineEditTrigger
					{...(props.disabled && {
						onClick: () =>
							toast.warning(
								props.disabledReason ?? 'You are not allowed to edit this field'
							),
					})}
					className={cn(
						'underline-offset-4 hover:underline hover:decoration-dashed',
						{ 'text-primary': isEdited },
						props.className
					)}
				>
					<div className='cell-data-number'>-</div>
				</InlineEditTrigger>
			) : (
				<InlineEditTrigger
					{...(props.disabled && {
						onClick: () =>
							toast.warning(
								props.disabledReason ?? 'You are not allowed to edit this field'
							),
					})}
					className={cn({ 'text-primary': isEdited }, props.className)}
				>
					<div className='underline-offset-4 hover:underline hover:decoration-dashed'>
						<BasicNumberField {...props} value={props.value} />
					</div>
				</InlineEditTrigger>
			)}
			<InlineEditInput>
				<Input
					disabled={props.disabled}
					type='number'
					defaultValue={props.value as number}
					value={editValue as string}
					autoFocus
					onChange={onChange}
				/>
			</InlineEditInput>
		</InlineEdit>
	)
}
